import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import TypographyComponent from 'components/TypographyComponent';
import BasicLayout from '../_components/BasicLayout';
import { LoadingButton } from '@mui/lab';
import { CardContent, Grid2, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import TextFieldComponent from 'components/TextFieldComponent';
import { useGetViaCepAdress } from 'services/ViaCepService/hooks/useMutation';
import { AppContext } from 'context/AppContextProvider';
import { useAuthenticationContext } from '../context/AuthenticationContextProvider';
import { RegisterFormSchema } from 'services/AuthService/schemas';
import { z } from 'zod';
import testeSvg from 'assets/images/svgs/SignInSVGS/2.svg';

type SignUpFormData = z.infer<typeof RegisterFormSchema>;

const SignUpPage = () => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useAuthenticationContext();

  const [useCnpj, setUseCnpj] = useState(false);
  const [showFirstColumn, setShowFirstColumn] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 900);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCheckboxChange = () => {
    setUseCnpj(!useCnpj);
  };

  const handleNext = () => {
    if (isMobileView) setShowFirstColumn(false);
  };

  const handleBack = () => {
    if (isMobileView) setShowFirstColumn(true);
  };

  const { mutateAsync: mutateAsyncCep } = useGetViaCepAdress();

  const handlePostalCodeBlur = async (postalCode: string) => {
    if (!postalCode) return;

    try {
      const address = await mutateAsyncCep(postalCode);
      setValue('postalCode', address.postalCode);
      setValue('city', address.city);
      setValue('state', address.state);
      setValue('street', address.street);
      setValue('neighborhood', address.neighborhood);
    } catch (error) {
      console.error('Erro ao buscar endereço:', error);
    }
  };

  const { showSnackbar } = useContext(AppContext);

  const onSubmit = (data: SignUpFormData) => {
    if (data) {
      navigate('/app/authentication/signUpProduct');
    }
  };

  return (
    <BasicLayout>
      <Card
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-around',
          position: 'absolute',
          padding: 3,
          backgroundColor: 'transparent',
        }}
      >
        {window.innerWidth > 1500 && (
          <Grid2
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'center'}
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            width={'55%'}
            sx={{ height: '100%' }}
          >
            <img src={testeSvg} alt="Desenho" width={450} />
            <p color="grey">Ja tem uma conta?</p>
            <Box mt={1}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loadingPosition="start"
                href="signIn"
              >
                Entrar
              </LoadingButton>
            </Box>
          </Grid2>
        )}
        <Grid2
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            height: '100%',
            width: '100%',
            display: { xs: 'flex', md: 'flex' },
            flexDirection: { xs: 'column', md: 'column' },
            transform: {
              xs:
                isMobileView && !showFirstColumn
                  ? 'translateX(-0%)'
                  : 'translateX(0)',
            },
            alignContent: 'center',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CardContent
            sx={{
              width: { sm: '100%', md: '30%' },
              height: '100%',
              display: isMobileView && !showFirstColumn ? 'none' : 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              paddingLeft: { md: '1vh', sm: '8vh', xs: '0vh' },
              paddingRight: { md: '1vh', sm: '8vh', xs: '0vh' },
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'transparent',
            }}
          >
            <Box
              pt={4}
              pb={3}
              sx={{
                width: { xs: '120%', md: '100%' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  marginY={'5px'}
                  color="white"
                >
                  <Typography color="white" variant="h4" margin={2}>
                    Cadastrar
                  </Typography>
                </Box>
                <Box
                  component="form"
                  role="form"
                  onSubmit={handleSubmit(onSubmit)}
                  sx={{ minWidth: '100%' }}
                >
                  <Box mb={-1}>
                    <Controller
                      control={control}
                      name={'name'}
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.name?.message}
                          type="text"
                          label="Nome"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box mb={-1}>
                    <Controller
                      control={control}
                      name={'email'}
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.email?.message}
                          type="email"
                          label="Email"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box mb={-1}>
                    <Controller
                      control={control}
                      name={'password'}
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.password?.message}
                          type="password"
                          label="Senha"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Checkbox
                      checked={useCnpj}
                      onChange={handleCheckboxChange}
                      inputProps={{ 'aria-label': 'Toggle CPF/CNPJ' }}
                    />
                    <TypographyComponent variant="button" fontWeight="regular">
                      Pessoa Juridica?
                    </TypographyComponent>
                  </Box>
                  {useCnpj ? (
                    <Box mb={2} width={'100%'}>
                      <Controller
                        control={control}
                        name={'cnpj'}
                        render={({ field: { value, onChange } }) => (
                          <TextFieldComponent
                            value={value}
                            onChangeText={onChange}
                            errorMessage={errors.cnpj?.message}
                            type="text"
                            label="CNPJ"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                  ) : (
                    <Box
                      mb={2}
                      display="flex"
                      alignItems="center"
                      width={'100%'}
                    >
                      <Controller
                        control={control}
                        name={'cpf'}
                        render={({ field: { value, onChange } }) => (
                          <TextFieldComponent
                            value={value}
                            onChangeText={onChange}
                            errorMessage={errors.cpf?.message}
                            type="text"
                            label="CPF"
                            sx={{ width: '100%' }}
                          />
                        )}
                      />
                    </Box>
                  )}
                  {window.innerWidth < 900 && (
                    <Box mt={1} mb={-4}>
                      <LoadingButton
                        type="button"
                        variant="contained"
                        color="primary"
                        loadingPosition="start"
                        fullWidth
                        onClick={handleNext}
                      >
                        Próximo
                      </LoadingButton>
                      {window.innerWidth < 900 && (
                        <LoadingButton
                          type="button"
                          variant="outlined"
                          color="primary"
                          loadingPosition="start"
                          fullWidth
                          onClick={() => navigate('/signIn')}
                          sx={{
                            marginTop: '1vh',
                          }}
                        >
                          Entrar
                        </LoadingButton>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
          <CardContent
            sx={{
              width: { sm: '100%', md: '30%' },
              height: '100%',
              display: isMobileView && showFirstColumn ? 'none' : 'flex',
              flexDirection: { xs: 'column', md: 'column' },
              paddingLeft: { md: '1vh', sm: '8vh', xs: '0vh' },
              paddingRight: { md: '1vh', sm: '8vh', xs: '0vh' },
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'transparent',
            }}
          >
            <Box
              pt={4}
              pb={3}
              sx={{
                width: { xs: '100%', md: '100%' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box
                  component="form"
                  role="form"
                  onSubmit={handleSubmit(onSubmit)}
                  sx={{ minWidth: '100%' }}
                >
                  <Box mb={-1}>
                    <Controller
                      control={control}
                      name="postalCode"
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          onBlur={() => handlePostalCodeBlur(value)}
                          errorMessage={errors.postalCode?.message}
                          type="text"
                          label="CEP"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box mb={-1} display="flex" alignItems="center">
                    <Controller
                      control={control}
                      name="city"
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.city?.message}
                          type="text"
                          label="Município"
                          fullWidth
                        />
                      )}
                    />
                    <Box ml={2}>
                      <Controller
                        control={control}
                        name="state"
                        render={({ field: { value, onChange } }) => (
                          <TextFieldComponent
                            value={value}
                            onChangeText={onChange}
                            errorMessage={errors.state?.message}
                            type="text"
                            label="Estado"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box mb={-1} display="flex" alignItems="center">
                    <Controller
                      control={control}
                      name="street"
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.street?.message}
                          type="text"
                          label="Endereço"
                          fullWidth
                        />
                      )}
                    />
                    <Box ml={1}>
                      <Controller
                        control={control}
                        name="addrNumber"
                        render={({ field: { value, onChange } }) => (
                          <TextFieldComponent
                            value={value}
                            onChangeText={onChange}
                            errorMessage={errors.addrNumber?.message}
                            type="text"
                            label="Número"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box mb={1}>
                    <Controller
                      control={control}
                      name="neighborhood"
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          value={value}
                          onChangeText={onChange}
                          errorMessage={errors.neighborhood?.message}
                          type="text"
                          label="Bairro"
                          fullWidth
                        />
                      )}
                    />
                  </Box>

                  <Box mt={1} mb={-4}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loadingPosition="start"
                      fullWidth
                      onClick={() => {
                        handleSubmit(onSubmit)();
                      }}
                    >
                      Proximo
                    </LoadingButton>
                    {window.innerWidth < 900 && (
                      <LoadingButton
                        type="button"
                        variant="outlined"
                        color="primary"
                        loadingPosition="start"
                        fullWidth
                        onClick={handleBack}
                        sx={{
                          marginTop: '1vh',
                        }}
                      >
                        Voltar
                      </LoadingButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Grid2>
      </Card>
    </BasicLayout>
  );
};

export default SignUpPage;
